import dayjs from "dayjs"
import { Analytics, logEvent } from "firebase/analytics"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import { AmountType } from "../Enum"
import {
  PDF_FONT_FAMILY_COURIER_BOLD,
  PDF_FONT_FAMILY_DEFAULT,
  PDF_FONT_FAMILY_DEFAULT_BOLD,
  PDF_FONT_FAMILY_TIMES_BOLD,
  PDF_FONT_SIZE_DEFAULT,
  PDF_FONT_SIZE_H1,
  PDF_FONT_SIZE_H2,
  PDF_FONT_SIZE_H3,
  PDF_FONT_SIZE_H4,
  PDF_ROW_SPACE,
  PDF_TEXT_COLOR_BLACK,
  PDF_TEXT_COLOR_GRAY,
} from "../Style"
import {
  Buyer,
  Inventory,
  Invoice,
  InvoiceItem,
  Record,
  RecordType,
} from "../Type"
import { formatInvoiceNumber, getRecordType } from "../Utils"
import { formatCurrencyAmount, getTotalPrice } from "./CurrencyUtils"
import { formatDate } from "./DateUtils"
import { BOOKKEEPER_BASE_64_ICON } from "./ImageUtils"

/**
 * Generates invoice rows for drawing on PDF report
 * @param objArray
 * @returns
 */
export const getInvoiceListPdfBody = (invoices: Invoice[], buyers: Buyer[]) => {
  // body array
  var body = []
  // Loop through each item
  for (let i = 0; i < invoices.length; i++) {
    // Each row
    let line = []
    // For each row, loop through all columns
    line[0] = formatDate(dayjs(invoices[i].date).valueOf())
    line[1] = buyers.filter((buyer) => buyer.id === invoices[i].buyerId)[0]
      ? buyers.filter((buyer) => buyer.id === invoices[i].buyerId)[0].name
      : ""
    line[2] = invoices[i].totalItems
    line[3] = formatCurrencyAmount(invoices[i].totalPrice)
    body[i] = line
  }
  return body
}

/**
 * https://github.com/simonbengtsson/jsPDF-AutoTable/blob/0866f83ac4731827f8eebb802b3b55cb08105d09/examples/examples.js#L190-L225
 */
export const exportInvoiceListAsPdf = (
  invoices: Invoice[],
  buyers: Buyer[],
  analytics: Analytics
) => {
  var doc = new jsPDF()
  // AutoTable is a separate dependency which needs to be installed separately
  autoTable(doc, {
    head: [["Date", "Customer", "Items", "Price (BDT)"]],
    body: getInvoiceListPdfBody(invoices, buyers),
    headStyles: {
      fillColor: [255, 153, 51],
    },
    didDrawPage: function (data: { settings: { margin: { left: number } } }) {
      // Header
      doc.setFontSize(20)
      doc.setTextColor(40)
      // doc.setFontStyle("normal")
      doc.addImage(
        BOOKKEEPER_BASE_64_ICON,
        "JPEG",
        data.settings.margin.left,
        15,
        10,
        10
      )
      // Bookkeeper page title
      // Not possible to display Bengali in jspdf
      doc.text("Invoices", data.settings.margin.left + 15, 22)

      // Footer
      var str = "Page " + (doc.internal.pages.length - 1)

      // not possible to display Bengali in jspdf
      str = str + "  (Developed by: https://banglabookkeeper.web.app)"
      doc.setFontSize(10)

      var pageSize = doc.internal.pageSize
      var pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight()
      doc.text(str, data.settings.margin.left, pageHeight - 10)
    },
    margin: { top: 30 },
  })

  const fileName = dayjs().format("DD_MM_YYYY") + "_InvoiceList.pdf"
  doc.save(fileName)
  logEvent(analytics, "web_pdf_invoices_exported")
}

/**
 * Generates records rows for drawing on PDF report
 * @param objArray
 * @returns
 */
const getRecordListPdfBody = (records: Record[], recordTypes: RecordType[]) => {
  // body array
  var body = []
  // Loop through each record
  for (let i = 0; i < records.length; i++) {
    // Each rec row
    let line = []
    // For each record row, loop through all columns
    line[0] = dayjs(records[i].date).format("DD/MM/YYYY")
    line[1] = formatCurrencyAmount(records[i].amount)
    line[2] = records[i].credit ? AmountType.Credit : AmountType.Debit
    line[3] = getRecordType(records[i], recordTypes).name
    body[i] = line
  }
  return body
}

/**
 * https://github.com/simonbengtsson/jsPDF-AutoTable/blob/0866f83ac4731827f8eebb802b3b55cb08105d09/examples/examples.js#L190-L225
 */
export const exportRecordListAsPdf = (
  records: Record[],
  recordTypes: RecordType[],
  analytics: Analytics
) => {
  var doc = new jsPDF()
  // AutoTable is a separate dependency which needs to be installed separately
  autoTable(doc, {
    head: [["Date", "Amount", "Category", "Type"]],
    body: getRecordListPdfBody(records, recordTypes),
    headStyles: {
      fillColor: [255, 153, 51],
    },
    didDrawPage: function (data: { settings: { margin: { left: number } } }) {
      // Header
      doc.setFontSize(20)
      doc.setTextColor(40)
      // doc.setFontStyle("normal")
      doc.addImage(
        BOOKKEEPER_BASE_64_ICON,
        "JPEG",
        data.settings.margin.left,
        15,
        10,
        10
      )
      // Bookkeeper page title
      // Not possible to display Bengali in jspdf
      doc.text("Credit & Debit", data.settings.margin.left + 15, 22)

      // Footer
      var str = "Page " + (doc.internal.pages.length - 1)

      // not possible to display Bengali in jspdf
      str = str + "  (Developed by: https://banglabookkeeper.web.app)"
      doc.setFontSize(10)

      var pageSize = doc.internal.pageSize
      var pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight()
      doc.text(str, data.settings.margin.left, pageHeight - 10)
    },
    margin: { top: 30 },
  })

  const fileName = dayjs().format("DD_MM_YYYY") + "_CreditDebit.pdf"
  doc.save(fileName)
  logEvent(analytics, "web_pdf_records_exported")
}

/**
 * Generates inventory rows for drawing on PDF report
 * @param objArray
 * @returns
 */
const getInventoryListPdfBody = (inventories: Inventory[]) => {
  // body array
  var body = []
  // Loop through each record
  for (let i = 0; i < inventories.length; i++) {
    // Each inventory row
    let line = []
    // For each inventory row, loop through all columns
    line[0] = inventories[i].name
    line[1] = formatCurrencyAmount(inventories[i].unitPrice)
    line[2] = inventories[i].quantity
    body[i] = line
  }
  return body
}

/**
 * https://github.com/simonbengtsson/jsPDF-AutoTable/blob/0866f83ac4731827f8eebb802b3b55cb08105d09/examples/examples.js#L190-L225
 */
export const exportInventoryListAsPdf = (
  inventories: Inventory[],
  analytics: Analytics
) => {
  var doc = new jsPDF()
  // AutoTable is a separate dependency which needs to be installed separately
  autoTable(doc, {
    head: [["Name", "Unit Price (BDT)", "Quantity"]],
    body: getInventoryListPdfBody(inventories),
    headStyles: {
      fillColor: [255, 153, 51],
    },
    didDrawPage: function (data: { settings: { margin: { left: number } } }) {
      // Header
      doc.setFontSize(20)
      doc.setTextColor(40)
      // doc.setFontStyle("normal")
      doc.addImage(
        BOOKKEEPER_BASE_64_ICON,
        "JPEG",
        data.settings.margin.left,
        15,
        10,
        10
      )
      // Bookkeeper page title
      // Not possible to display Bengali in jspdf
      doc.text("Inventories", data.settings.margin.left + 15, 22)

      // Footer
      var str = "Page " + (doc.internal.pages.length - 1)

      // not possible to display Bengali in jspdf
      str = str + "  (Developed by: https://banglabookkeeper.web.app)"
      doc.setFontSize(10)

      var pageSize = doc.internal.pageSize
      var pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight()
      doc.text(str, data.settings.margin.left, pageHeight - 10)
    },
    margin: { top: 30 },
  })

  const fileName = dayjs().format("DD_MM_YYYY") + "_InventoryList.pdf"
  doc.save(fileName)
  logEvent(analytics, "web_pdf_inventories_exported")
}

/**
 * Generates buyer rows for drawing on PDF report
 * @param objArray
 * @returns
 */
const getBuyerListPdfBody = (buyers: Buyer[]) => {
  // body array
  var body = []
  // Loop through each record
  for (let i = 0; i < buyers.length; i++) {
    // Each row
    let line = []
    // For each row, loop through all columns
    line[0] = buyers[i].name
    line[1] = buyers[i].address
    line[2] = buyers[i].phone
    line[3] = buyers[i].email
    line[4] = buyers[i].website
    body[i] = line
  }
  return body
}

/**
 * https://github.com/simonbengtsson/jsPDF-AutoTable/blob/0866f83ac4731827f8eebb802b3b55cb08105d09/examples/examples.js#L190-L225
 */
export const exportBuyerListAsPdf = (buyers: Buyer[], analytics: Analytics) => {
  var doc = new jsPDF()
  // AutoTable is a separate dependency which needs to be installed separately
  autoTable(doc, {
    head: [["Name", "Address", "Phone", "Email", "Website"]],
    body: getBuyerListPdfBody(buyers),
    headStyles: {
      fillColor: [255, 153, 51],
    },
    didDrawPage: function (data: { settings: { margin: { left: number } } }) {
      // Header
      doc.setFontSize(20)
      doc.setTextColor(40)
      // doc.setFontStyle("normal")
      doc.addImage(
        BOOKKEEPER_BASE_64_ICON,
        "JPEG",
        data.settings.margin.left,
        15,
        10,
        10
      )
      // Bookkeeper page title
      // Not possible to display Bengali in jspdf
      doc.text("Customers", data.settings.margin.left + 15, 22)

      // Footer
      var str = "Page " + (doc.internal.pages.length - 1)

      // not possible to display Bengali in jspdf
      str = str + "  (Developed by: https://banglabookkeeper.web.app)"
      doc.setFontSize(10)

      var pageSize = doc.internal.pageSize
      var pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight()
      doc.text(str, data.settings.margin.left, pageHeight - 10)
    },
    margin: { top: 30 },
  })

  const fileName = dayjs().format("DD_MM_YYYY") + "_CustomerList.pdf"
  doc.save(fileName)
  logEvent(analytics, "web_pdf_buyers_exported")
}

/**
 * Generates invoice item rows for drawing on PDF report
 * @param objArray
 * @returns
 */
const getInvoicePdfBody = (invoiceItems: InvoiceItem[]) => {
  // body array
  var body = []
  // Loop through each item
  for (let i = 0; i < invoiceItems.length; i++) {
    // Each row
    let line = []
    // For each row, loop through all columns
    line[0] = invoiceItems[i].name
    line[1] = invoiceItems[i].quantity
    line[2] = formatCurrencyAmount(invoiceItems[i].unitPrice)
    line[3] = formatCurrencyAmount(invoiceItems[i].totalPrice)
    body[i] = line
  }
  // Total line
  let line = []
  line[2] = "Total amount:"
  line[3] = `${formatCurrencyAmount(getTotalPrice(invoiceItems))} BDT`
  body[body.length] = line
  return body
}

/**
 * https://github.com/simonbengtsson/jsPDF-AutoTable/blob/0866f83ac4731827f8eebb802b3b55cb08105d09/examples/examples.js#L190-L225
 */
export const generateInvoicePdfFile = (
  invoiceItems: InvoiceItem[],
  name: string,
  addressLineOne: string,
  addressLineTwo: string,
  phone: string,
  email: string,
  buyerName: string,
  buyerPhone: string,
  buyerEmail: string,
  buyerAddressLineOne: string,
  buyerAddressLineTwo: string,
  analytics: Analytics
) => {
  var doc = new jsPDF()
  // AutoTable is a separate dependency which needs to be installed separately
  autoTable(doc, {
    head: [["Description", "Qty", "Unit Price (BDT)", "Amount (BDT)"]],
    body: getInvoicePdfBody(invoiceItems),
    headStyles: {
      fillColor: [255, 153, 51],
    },
    didDrawPage: function (data: { settings: { margin: { left: number } } }) {
      // Header
      // doc.addImage(
      //   BOOKKEEPER_BASE_64_ICON,
      //   "JPEG",
      //   data.settings.margin.left,
      //   12,
      //   12,
      //   12
      // )
      // IMPORTANT! Not possible to display Bengali in jspdf
      // FIRST COLUMN
      // Business Profile name
      let startPositionY = 21
      doc
        .setFont(PDF_FONT_FAMILY_TIMES_BOLD)
        .setFontSize(PDF_FONT_SIZE_H2)
        .setTextColor(PDF_TEXT_COLOR_BLACK)
        .text(
          name.toUpperCase(),
          data.settings.margin.left, //+ 15, // Move left to business icon
          startPositionY
        )

      // Business profile Address line one
      if (addressLineOne !== "") {
        startPositionY += 1.3 * PDF_ROW_SPACE
        doc
          .setFont(PDF_FONT_FAMILY_DEFAULT)
          .setFontSize(PDF_FONT_SIZE_DEFAULT)
          .setTextColor(PDF_TEXT_COLOR_BLACK)
          .text(addressLineOne, data.settings.margin.left, startPositionY)
      }

      // Business profile Address line two
      if (addressLineTwo !== "") {
        startPositionY += PDF_ROW_SPACE
        doc
          .setFont(PDF_FONT_FAMILY_DEFAULT)
          .setFontSize(PDF_FONT_SIZE_DEFAULT)
          .setTextColor(PDF_TEXT_COLOR_BLACK)
          .text(addressLineTwo, data.settings.margin.left, startPositionY)
      }

      // Business profile Phone
      if (addressLineOne === "" && addressLineTwo === "") {
        startPositionY += 1.3 * PDF_ROW_SPACE
      } else {
        startPositionY += PDF_ROW_SPACE
      }
      doc
        .setFont(PDF_FONT_FAMILY_DEFAULT)
        .setFontSize(PDF_FONT_SIZE_DEFAULT)
        .setTextColor(PDF_TEXT_COLOR_BLACK)
        .text(`Phone: ${phone}`, data.settings.margin.left, startPositionY)

      // Business profile Email
      startPositionY += PDF_ROW_SPACE
      doc
        .setFont(PDF_FONT_FAMILY_DEFAULT)
        .setFontSize(PDF_FONT_SIZE_DEFAULT)
        .setTextColor(PDF_TEXT_COLOR_BLACK)
        .text(`Email: ${email}`, data.settings.margin.left, startPositionY)

      // BILL TO section title
      startPositionY += 2.2 * PDF_ROW_SPACE
      doc
        .setFont(PDF_FONT_FAMILY_DEFAULT_BOLD)
        .setFontSize(PDF_FONT_SIZE_H3)
        // .setDrawColor(0)
        // .setFillColor(255, 153, 51)
        // .rect(data.settings.margin.left, startPositionY - 5, 30, 6, "F") //Fill and Border
        .setTextColor(PDF_TEXT_COLOR_BLACK)
        .text("BILL TO:", data.settings.margin.left, startPositionY)

      // SHIP TO section title
      let shipTostartPositionX = data.settings.margin.left + 68
      doc
        .setFont(PDF_FONT_FAMILY_DEFAULT_BOLD)
        .setFontSize(PDF_FONT_SIZE_H3)
        // .setDrawColor(0)
        // .setFillColor(255, 153, 51)
        // .rect(shipTostartPositionX, startPositionY - 5, 30, 6, "F") //Fill and Border
        .setTextColor(PDF_TEXT_COLOR_BLACK)
        .text("SHIP TO:", shipTostartPositionX, startPositionY)

      // BILL TO Business name
      startPositionY += 1.5 * PDF_ROW_SPACE
      doc
        .setFont(PDF_FONT_FAMILY_COURIER_BOLD)
        .setFontSize(PDF_FONT_SIZE_H4)
        .setTextColor(PDF_TEXT_COLOR_BLACK)
        .text(buyerName, data.settings.margin.left, startPositionY)

      // SHIP TO Business name
      doc
        .setFont(PDF_FONT_FAMILY_COURIER_BOLD)
        .setFontSize(PDF_FONT_SIZE_H4)
        .setTextColor(PDF_TEXT_COLOR_BLACK)
        .text(buyerName, shipTostartPositionX, startPositionY)

      // BILL TO Address line one
      if (buyerAddressLineOne !== "") {
        startPositionY += PDF_ROW_SPACE
        doc
          .setFont(PDF_FONT_FAMILY_DEFAULT)
          .setFontSize(PDF_FONT_SIZE_DEFAULT)
          .setTextColor(PDF_TEXT_COLOR_BLACK)
          .text(buyerAddressLineOne, data.settings.margin.left, startPositionY)
      }

      // SHIP TO Address line one
      if (buyerAddressLineOne !== "") {
        doc
          .setFont(PDF_FONT_FAMILY_DEFAULT)
          .setFontSize(PDF_FONT_SIZE_DEFAULT)
          .setTextColor(PDF_TEXT_COLOR_BLACK)
          .text(buyerAddressLineOne, shipTostartPositionX, startPositionY)
      }

      // BILL TO: Address line two
      if (buyerAddressLineTwo !== "") {
        startPositionY += PDF_ROW_SPACE
        doc
          .setFont(PDF_FONT_FAMILY_DEFAULT)
          .setFontSize(PDF_FONT_SIZE_DEFAULT)
          .setTextColor(PDF_TEXT_COLOR_BLACK)
          .text(buyerAddressLineTwo, data.settings.margin.left, startPositionY)
      }

      // SHIP TO: Address line two
      if (buyerAddressLineTwo !== "") {
        doc
          .setFont(PDF_FONT_FAMILY_DEFAULT)
          .setFontSize(PDF_FONT_SIZE_DEFAULT)
          .setTextColor(PDF_TEXT_COLOR_BLACK)
          .text(buyerAddressLineTwo, shipTostartPositionX, startPositionY)
      }

      // BILL TO Phone
      if (buyerPhone !== "") {
        startPositionY += PDF_ROW_SPACE
        doc
          .setFont(PDF_FONT_FAMILY_DEFAULT)
          .setFontSize(PDF_FONT_SIZE_DEFAULT)
          .setTextColor(PDF_TEXT_COLOR_BLACK)
          .text(
            `Phone: ${buyerPhone}`,
            data.settings.margin.left,
            startPositionY
          )
      }

      // SHIP TO Phone
      if (buyerPhone !== "") {
        doc
          .setFont(PDF_FONT_FAMILY_DEFAULT)
          .setFontSize(PDF_FONT_SIZE_DEFAULT)
          .setTextColor(PDF_TEXT_COLOR_BLACK)
          .text(`Phone: ${buyerPhone}`, shipTostartPositionX, startPositionY)
      }

      // BILL TO Email
      if (buyerEmail !== "") {
        startPositionY += PDF_ROW_SPACE
        doc
          .setFont(PDF_FONT_FAMILY_DEFAULT)
          .setFontSize(PDF_FONT_SIZE_DEFAULT)
          .setTextColor(PDF_TEXT_COLOR_BLACK)
          .text(
            `Email: ${buyerEmail}`,
            data.settings.margin.left,
            startPositionY
          )
      }

      // SHIP TO Email
      if (buyerEmail !== "") {
        doc
          .setFont(PDF_FONT_FAMILY_DEFAULT)
          .setFontSize(PDF_FONT_SIZE_DEFAULT)
          .setTextColor(PDF_TEXT_COLOR_BLACK)
          .text(`Email: ${buyerEmail}`, shipTostartPositionX, startPositionY)
      }

      // SECOND COLUMN
      // INVOICE
      let startPositionX = data.settings.margin.left + 120
      let valueStartPositionX = data.settings.margin.left + 145
      startPositionY = 22
      doc
        .setFont(PDF_FONT_FAMILY_TIMES_BOLD)
        .setFontSize(PDF_FONT_SIZE_H1)
        .setTextColor(PDF_TEXT_COLOR_GRAY)
        .text("INVOICE", startPositionX + 10, startPositionY)

      // Date title
      startPositionY += 2 * PDF_ROW_SPACE
      doc
        .setFont(PDF_FONT_FAMILY_DEFAULT_BOLD)
        .setFontSize(PDF_FONT_SIZE_H4)
        .setTextColor(PDF_TEXT_COLOR_BLACK)
        .text("Date:", startPositionX, startPositionY)

      doc
        .setFont(PDF_FONT_FAMILY_DEFAULT)
        .setFontSize(PDF_FONT_SIZE_DEFAULT)
        .setTextColor(PDF_TEXT_COLOR_BLACK)
        .text(
          formatDate(dayjs().valueOf()),
          valueStartPositionX,
          startPositionY
        )

      // Invoice number title
      startPositionY += PDF_ROW_SPACE
      doc
        .setFont(PDF_FONT_FAMILY_DEFAULT_BOLD)
        .setFontSize(PDF_FONT_SIZE_H4)
        .setTextColor(PDF_TEXT_COLOR_BLACK)
        .text("Invoice no.", startPositionX, startPositionY)

      doc
        .setFont(PDF_FONT_FAMILY_DEFAULT)
        .setFontSize(PDF_FONT_SIZE_DEFAULT)
        .setTextColor(PDF_TEXT_COLOR_BLACK)
        .text(formatInvoiceNumber(), valueStartPositionX, startPositionY)

      // Due date title
      startPositionY += PDF_ROW_SPACE
      doc
        .setFont(PDF_FONT_FAMILY_DEFAULT_BOLD)
        .setFontSize(PDF_FONT_SIZE_H4)
        .setTextColor(PDF_TEXT_COLOR_BLACK)
        .text("Due Date:", startPositionX, startPositionY)

      doc
        .setFont(PDF_FONT_FAMILY_DEFAULT)
        .setFontSize(PDF_FONT_SIZE_DEFAULT)
        .setTextColor(PDF_TEXT_COLOR_BLACK)
        .text(
          formatDate(dayjs().add(2, "week").valueOf()),
          valueStartPositionX,
          startPositionY
        )

      // FOOTER
      doc.setFont(PDF_FONT_FAMILY_DEFAULT)
      var str = "Page " + (doc.internal.pages.length - 1)

      // not possible to display Bengali in jspdf
      str =
        str +
        "  Developed by Bookkeeper (website: https://banglabookkeeper.web.app)"
      doc.setFontSize(10)

      var pageSize = doc.internal.pageSize
      var pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight()
      doc.text(str, data.settings.margin.left, pageHeight - 10)
    },
    margin: { top: 85 },
  })

  const fileName = dayjs().format("DD_MM_YYYY") + "_Invoice.pdf"
  doc.save(fileName)
  logEvent(analytics, "web_pdf_invoices_exported")
}
